import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, Switch, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getExportJob,
  createExportJob,
  updateExportJob,
  ImportFileFormats,
  DataManagementModules,
} from '@/api/dataManagementApi';
import { useAuth } from '@/hooks/useAuth';

export default function ExportJobForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = !!id;
  const canWrite = hasPermission('data_management', 'write');

  useEffect(() => {
    if (!canWrite) navigate('/data-management/exports');
  }, [canWrite, navigate]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await getExportJob(id);
        if (res.data?.success && res.data.data) {
          form.setFieldsValue(res.data.data);
        }
      } catch {
        message.error(t('app.error'));
      }
    })();
  }, [id, form, t]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      if (isEdit) {
        await updateExportJob(id!, values);
        message.success(t('data_management.export.update_success'));
      } else {
        await createExportJob(values);
        message.success(t('data_management.export.create_success'));
      }
      navigate('/data-management/exports');
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ file_format: 'csv', include_headers: true, sort_descending: false }}
      >
        <Form.Item
          name="name"
          label={t('data_management.export.field.name')}
          rules={[{ required: true, max: 200 }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('data_management.export.field.description')}
          rules={[{ max: 1000 }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        {!isEdit && (
          <Form.Item
            name="source_module"
            label={t('data_management.export.field.source_module')}
            rules={[{ required: true }]}
          >
            <Select
              options={DataManagementModules.map((m) => ({
                value: m,
                label: t(`data_management.import.modules.${m}`),
              }))}
            />
          </Form.Item>
        )}

        <Form.Item
          name="file_format"
          label={t('data_management.export.field.file_format')}
          rules={[{ required: true }]}
        >
          <Select
            disabled={isEdit}
            options={ImportFileFormats.map((f) => ({
              value: f,
              label: t(`data_management.import.formats.${f}`),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="columns"
          label={t('data_management.export.field.columns')}
        >
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item
          name="sort_field"
          label={t('data_management.export.field.sort_field')}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="sort_descending"
          label={t('data_management.export.field.sort_descending')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="include_headers"
          label={t('data_management.export.field.include_headers')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEdit ? t('app.save') : t('app.create')}
            </Button>
            <Button onClick={() => navigate('/data-management/exports')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
